import { useState } from "react";
import type { ProductWork, ProductWorkItem, WorkByProductResponse } from "../types";
import Rel from "../atoms/Rel";
import "./PanelProductWork.css";

// BOARDROOM-2026-08-08-135707: per-product project pages. Renders each
// product's active work DERIVED from live _dispatch/_completion/_escalation
// traffic (GET /api/work-by-product), replacing/augmenting the dead static
// project-row kanban -- the programme moved to dispatch-driven flow ~June,
// so unmaintained project rows are not a reliable "what's happening now"
// signal (see PanelProjectsKanban for the corrected legacy view, kept for
// the WU-level token/ETA detail this per-product view does not carry).

const PRODUCT_ORDER = [
  "my.trumin",
  "manage.trumin",
  "timing",
  "mobile",
  "tickets",
  "payments",
  "marketing",
  "infra",
  "comms",
  "datalake",
];

function isoToEpochMs(iso: string | null): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

function statusLabel(status: ProductWorkItem["status"]): string {
  switch (status) {
    case "in-flight":
      return "In flight";
    case "done":
      return "Done";
    case "blocked":
      return "Blocked";
    default:
      return status;
  }
}

function WorkItemRow({ item }: { item: ProductWorkItem }) {
  return (
    <div className={`pwrow pwrow-${item.status}`} data-section-id={item.section_id}>
      <span className={`pwrow-status pwrow-status-${item.status}`}>
        {statusLabel(item.status)}
      </span>
      <span className="pwrow-title">{item.title}</span>
      <span className="pwrow-ref">{item.section_id}</span>
      <span className="pwrow-time">
        <Rel t={isoToEpochMs(item.timestamp)} />
      </span>
    </div>
  );
}

function ProductTabPanel({ product, work }: { product: string; work?: ProductWork }) {
  if (!work) {
    return <div className="panel-empty">No data for {product} yet.</div>;
  }
  if (work.error) {
    return (
      <div className="panel-empty pwrow-error">
        {product} fetch failed: {work.error}
      </div>
    );
  }
  if (work.items.length === 0) {
    return <div className="panel-empty">No recent work for {product}.</div>;
  }
  return (
    <div className="pw-list">
      {work.items.map((item) => (
        <WorkItemRow key={item.section_id || `${item.title}-${item.timestamp}`} item={item} />
      ))}
    </div>
  );
}

export default function PanelProductWork({
  workByProduct,
}: {
  workByProduct: WorkByProductResponse | null;
}) {
  const products = workByProduct?.products ?? {};
  // Preserve a stable, meaningful order; append any product the backend
  // returns that isn't in PRODUCT_ORDER (defensive -- never hide a product
  // just because this frontend constant predates a roster change).
  const knownOrder = PRODUCT_ORDER.filter((p) => p in products);
  const extra = Object.keys(products).filter((p) => !PRODUCT_ORDER.includes(p));
  const order = [...knownOrder, ...extra];

  const [active, setActive] = useState<string>(order[0] ?? "my.trumin");
  const activeProduct = order.includes(active) ? active : order[0];

  const totalItems = Object.values(products).reduce(
    (sum, w) => sum + w.items.length,
    0,
  );

  return (
    <section
      className={`panel panel-b panel-product-work ${order.length === 0 ? "is-empty" : "is-populated"}`}
      aria-labelledby="panel-product-work-title"
    >
      <header className="panel-head kanban-head">
        <div className="kanban-head-left">
          <h2 className="panel-title" id="panel-product-work-title">
            Projects by product
          </h2>
          {order.length > 0 && (
            <nav
              className="kanban-tabs pw-tabs"
              role="tablist"
              aria-label="Product"
            >
              {order.map((p) => (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  id={`pw-tab-${p}`}
                  aria-selected={activeProduct === p}
                  aria-controls={`pw-tabpanel-${p}`}
                  tabIndex={activeProduct === p ? 0 : -1}
                  className={`kanban-tab ${activeProduct === p ? "is-active" : ""}`}
                  onClick={() => setActive(p)}
                >
                  <span className="kanban-tab-label">{p}</span>
                  <span className="kanban-tab-count">
                    {products[p]?.items.length ?? 0}
                  </span>
                </button>
              ))}
            </nav>
          )}
        </div>
        <span className="panel-meta">
          <span className="panel-count">{totalItems}</span>
          <span className="panel-sub">
            derived from dispatch/completion traffic
            {workByProduct?.since ? ` since ${workByProduct.since}` : ""}
          </span>
        </span>
      </header>
      <div
        className="panel-body"
        role="tabpanel"
        id={`pw-tabpanel-${activeProduct}`}
        aria-labelledby={`pw-tab-${activeProduct}`}
      >
        {order.length === 0 ? (
          <div className="panel-empty">
            No product work data yet — /api/work-by-product not reachable.
          </div>
        ) : (
          <ProductTabPanel product={activeProduct} work={products[activeProduct]} />
        )}
      </div>
    </section>
  );
}
